import React, { useCallback } from "react";
import _ from "lodash";
import { ColorX, LocaleX } from "IZOX/STATIC";
import { observer } from "mobx-react";
import { XText, HStack, XTooltip, XImage } from "IZOX/_X";

/**
 * @typedef {import("IZOX/_X/HStack").hstackparams} hstackparams
 * 
 * @typedef {Object} bavatarparams
 * @property {Object} user
 * @property {Function} onIconPress
 * @property {Function} onNamePress
 * @property {Function} onPress
 * @property {Boolean} showIcon
 * @property {Boolean} showName
 * @property {Number} size
 * @property {Number|String} width
 * @property {import("react").CSSProperties} style
 * @property {String} userColor - Optional color for the user's name text
 * 
 * @param {bavatarparams & hstackparams} param0 
 * @returns 
 */
function BAvatar({
  user,
  onIconPress,
  onNamePress,
  showIcon = true,
  showName = true,
  size = 30,
  width,
  style,
  userColor,
  disabled = false,
  ...others
}){

  let _style = {...style};

  const getCroppedName = useCallback(() => {
    let name = user?.userDisplayName;
    if(_.isEmpty(name)) return "";
    let splited = name.split(" ");
    let rtn = _.map(splited, o => o?.[0]?.toUpperCase?.()).join("").replace("(", "");
    return rtn;
  }, [user]);

  const renderName = useCallback(() => {
    return (
      <XTooltip tooltip={user.userDisplayName}>
        <XText bold fontSize={size} style={{maxWidth: width}} 
          colorTransition
          color={userColor ?? ColorX.DayNight({
            day: "gambot2Blue",
            night: "dmWhite"
          })}
          noWrap ellipsisMode="ellipsis">
          {user.userDisplayName}
        </XText>
      </XTooltip>
    );
  }, [user, size, width, userColor]);

  const renderNoName = useCallback(() => {
    return (
      <XText style={{color: ColorX.CSS("greyOut"), fontSize: size, width: width, textAlign: "center"}}>
        {LocaleX.Parse({
          EN: "No one yet",
          TC: "未有"
        })}
      </XText>
    );
  }, [size, width]);

  const renderIcon = useCallback(() => {
    let iconSize = size * 2;
    let imgCStyle = {
      borderRadius: 50,
      overflow: "hidden",
      width: iconSize, 
      height: iconSize,
      minWidth: iconSize, 
      minHeight: iconSize,
      maxWidth: iconSize, 
      maxHeight: iconSize
    };
    if(_.isEmpty(user?.staffPhoto)){
      let croppedName = getCroppedName();
      let randomBG = ColorX.GetAvatarRandom(user?.userDisplayName);
      return (
        <HStack bgColor={randomBG} round={50} width={size} style={imgCStyle}>
          <XText color={ColorX.CSS("pureWhite")} fontSize={size * 0.75}>
            {croppedName}
          </XText>
        </HStack>
      );
    }

    let source = `data:image/png;base64,${user?.staffPhoto}`;

    return (
      <XImage source={source} style={imgCStyle}/>
    );
  }, [user, size, getCroppedName]);

  if(_.isEmpty(user)){
    return (
      <HStack spacing={10} style={_style} width={width}>
        {renderNoName()}
      </HStack>
    );
  }
  return (
    <HStack spacing={10} style={_style} p={5}
      width={width} HAlign="[" {...others}>
      {showIcon && renderIcon()}
      {showName && renderName()}
    </HStack>
  ); 

}

export default observer(BAvatar);
